// --------------------------------------------------------------
// Created On: 2025-06-20
// Author: Dimitra Weinstein
//
// Last Modified: 2025-09-10
// Modified By: Jonathon Hicke
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useState, useMemo, useEffect } from "react";
import useApi from "../../hooks/useApi";
import { API } from "../../constants/miscellaneous";
import getApiError from "../../utilities/api/getApiError";
import Spinner from "../../components/Spinner/Spinner";
import Toast from "../../components/Toast/Toast";
import QuoteListForm from "./QuoteListForm/QuoteListForm";
import QuoteListTable from "./QuoteListTable/QuoteListTable";
import QuoteListModal from "./QuoteListModal/QuoteListModal";
import deepCopy from "../../utilities/deepCopy";
import nowToUtcIso from "../../utilities/time/nowToUtcIso";

// Main page for displaying a list of sales quotes.
export default function QuoteListPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [filter, setFilter] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<number>(0);
  const [sortColumnIndex, setSortColumnIndex] = useState<number>(3);
  const [sortAscending, setSortAscending] = useState<boolean>(false);
  const [salesQuotes, setSalesQuotes] = useState<SalesQuote[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number>(-1);
  const [selectedQuote, setSelectedQuote] = useState<SalesQuote | null>(null);
  const filteredSalesQuotes = useMemo(
    () => getFilteredSalesQuotes(salesQuotes, filter, dateFilter),
    [JSON.stringify(salesQuotes), filter, dateFilter, sortAscending, sortColumnIndex]
  );

  useEffect(() => {}, [sortAscending, sortColumnIndex]);

  // Fetches all sales quotes created by the user from the API.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      url: `${API}/sales-quote`,
      method: "GET",
      body: null,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setRowCount(responseBody.salesQuotes.length);
        setSalesQuotes(sortSalesQuotes(responseBody.salesQuotes));
        setFailedToLoad(false);
        setErrorMessage("");
      } else {
        setFailedToLoad(true);
        setErrorMessage(await getApiError(response, "Unable to retrieve sales quotes."));
      }
      setLoading(false);
    },
    []
  );

  // Filters the sales quotes based on the filter and date filter.
  function getFilteredSalesQuotes(salesQuotes: SalesQuote[], filter: string, dateFilter: number): SalesQuote[] {
    let salesQuotesDeepCopy = deepCopy(salesQuotes);
    if (filter.length > 0) {
      salesQuotesDeepCopy = salesQuotesDeepCopy.filter(
        (quote) => quote.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0
      );
    }
    if (dateFilter === 30) {
      const thirtyDaysAgo = nowToUtcIso(30);
      console.log(thirtyDaysAgo);
      salesQuotesDeepCopy = salesQuotesDeepCopy.filter((quote) => {
        if (quote.createdUtc >= thirtyDaysAgo || (quote.receivedUtc !== null && quote.receivedUtc >= thirtyDaysAgo)) {
          return true;
        } else {
          return false;
        }
      });
    } else if (dateFilter === 90) {
      const threeMonthsAgo = nowToUtcIso(90);
      salesQuotesDeepCopy = salesQuotesDeepCopy.filter((quote) => {
        if (quote.createdUtc >= threeMonthsAgo || (quote.receivedUtc !== null && quote.receivedUtc >= threeMonthsAgo)) {
          return true;
        } else {
          return false;
        }
      });
    } else if (dateFilter === 365) {
      const oneYearAgo = nowToUtcIso(365);
      salesQuotesDeepCopy = salesQuotesDeepCopy.filter((quote) => {
        if (quote.createdUtc >= oneYearAgo || (quote.receivedUtc !== null && quote.receivedUtc >= oneYearAgo)) {
          return true;
        } else {
          return false;
        }
      });
    } else {
      return sortSalesQuotes(salesQuotesDeepCopy);
    }
    return sortSalesQuotes(salesQuotesDeepCopy);
  }

  // Sorts the sales quotes based on the selected column and order.
  function sortSalesQuotes(salesQuotes: SalesQuote[]): SalesQuote[] {
    return salesQuotes.sort((a, b) => {
      let aValue: string = "N/A";
      let bValue: string = "N/A";

      switch (sortColumnIndex) {
        case 1:
          aValue = a.salesQuoteId.toString();
          bValue = b.salesQuoteId.toString();
          break;
        case 2:
          aValue = a.name;
          bValue = b.name;
          break;
        case 3:
          aValue = a.createdUtc;
          bValue = b.createdUtc;
          break;
        case 4:
          aValue = a.receivedUtc !== null ? a.receivedUtc.toString() : "N/A";
          bValue = b.receivedUtc !== null ? b.receivedUtc.toString() : "N/A";
          break;
        default:
          aValue = a.salesQuoteId.toString();
          bValue = b.salesQuoteId.toString();
      }

      if (aValue < bValue) {
        return sortAscending ? 1 : -1;
      } else if (aValue > bValue) {
        return sortAscending ? -1 : 1;
      } else {
        return 0;
      }
    });
  }

  // Changes the currently selected quote.
  function updateSelection(selectedQuoteId: number): void {
    setSelectedQuoteId(selectedQuoteId);
    const quote = salesQuotes.find((quote) => quote.salesQuoteId === selectedQuoteId);
    if (quote === undefined) {
      setSelectedQuote(null);
    } else {
      setSelectedQuote(quote);
    }
  }

  // Updates sorting order.
  function updateSort(newIndex: number): void {
    if (newIndex === sortColumnIndex) {
      setSortAscending((prev) => !prev);
    } else {
      setSortAscending(false);
      setSortColumnIndex(newIndex);
    }
  }

  return (
    <div className="container p-4">
      <Spinner loading={loading} />

      {failedToLoad && (
        <Toast
          type="error"
          title="Failed to load sales quote list"
          message={errorMessage}
          show={failedToLoad}
          onClose={() => {
            setFailedToLoad(false), setErrorMessage("");
          }}
        />
      )}

      <div data-test="sales-quotes-table-container" className="row">
        <QuoteListForm
          rowCount={rowCount}
          filter={filter}
          dateFilter={dateFilter}
          onChangeFilter={(filter) => setFilter(filter)}
          onChangeDateFilter={(dateFilter) => setDateFilter(dateFilter)}
        />

        <QuoteListTable
          loading={loading}
          salesQuotes={filteredSalesQuotes}
          sortColumnIndex={sortColumnIndex}
          sortAscending={sortAscending}
          onUpdateSort={(newIndex) => updateSort(newIndex)}
          onUpdateSelection={(salesQuoteId) => updateSelection(salesQuoteId)}
        />

        {selectedQuoteId > 0 && selectedQuote !== null && (
          <QuoteListModal
            loading={loading}
            showModal={selectedQuoteId > 0}
            selectedQuoteId={selectedQuoteId}
            quote={selectedQuote}
            onClose={() => updateSelection(-1)}
            onChangeLoading={(loading) => setLoading(loading)}
            onChangeFailedToLoad={(failedToLoad) => setFailedToLoad(failedToLoad)}
            onChangeErrorMessage={(errorMessage) => setErrorMessage(errorMessage)}
          />
        )}
      </div>
    </div>
  );
}

interface ResponseBody {
  salesQuotes: SalesQuote[];
}
interface SalesQuote {
  salesQuoteId: number;
  name: string;
  salesOrderId: number | null;
  createdUtc: string;
  receivedUtc: string | null;
}
